import { IEmployee, IEmployeeIndividualRate } from '../../settings/types/employee'
import { IDirectCost, IOfferDirectCost, IOfferPassThroughCost, IPassThroughCost, IProjectOfferPhase } from './directAndPassTroughCosts'
import { IProjectActivity } from './projectActivity'
import {ITimeTrackingProject} from "../../time-tracking/types/timeTracking";

export const projectStatus = [
  { key: 'on_track', name: 'On Track' },
  { key: 'off_track', name: 'Off Track' },
  { key: 'at_risk', name: 'At Risk' },
  { key: 'upcoming', name: 'Upcoming' },
  { key: 'completed', name: 'Completed' },
  { key: 'in_progress', name: 'In Progress' },
]
export type ProjectType =
  | ['sponsor', 'Sponsor']
  | ['service_provider', 'Service Provider']
  | ['internal', 'Internal']
  | ''
export type ProjectPermissions = ['restricted', 'Restricted Project'] | ['open', 'Open Project'] | ''

export interface IProject {
  id: string | number
  name: string
  customer?: ICustomer
  category?: string
  color: string
  type: 'sponsor' | 'service_provider' | 'internal' | ''
  permissions: 'restricted' | 'open' | ''
  employees: IEmployee[]
  start_date: string | number | Date | null
  end_date: string | number | Date | null
  budget: number
  phases: IProjectPhase[] | IProjectOfferPhase[]
  time_used: number
  budget_used: number
  invoiced: number
  health: 'on_track' | 'off_track' | 'at_risk' | ''
  healthColor: (health: string) => { color: string; background: string }
  activities: IProjectActivity[]
  direct_costs: IDirectCost[]
  active: boolean
  labels: IProjectLabel[]
  time_tracking_perm: ITimeTrackingPermission
  individual_employee_rate: IEmployeeIndividualRate[]
  project_billing_rate: string
  billing_procedure: 'scheduled_payment' | 'billing_by_tasks' | 'billing_on_actual'
  cost_rate_billing: IProjectCostCategory[]
  project_billing_rate_id: string | number
  accounting: boolean
  deleted_on: string | number | Date | null
  inactive_on: string | number | Date | null
  description?: string
  rebate?: number
  admins: IEmployee[]
  booked_employees?: IEmployee[]
  booked_employees_info?: IEmployee[]
  created_at?: string | number | Date | null
  offers: IProjectOffer[]
  expenditures_budget_widget: number
  recruitment?: boolean
  target_patients?: number
  target_sites?: number
  current_patients?: number
  current_sites?: number
  notifications: number[]
  project_forecast?: number
  financial_calculation: IProjectFinancialCalculation
}

export interface IProjectOffer {
  id: number | string
  version: string
  date: string | number | Date
  project: IProject
  directCosts?: IOfferDirectCost[]
  passThroughCosts?: IOfferPassThroughCost[]
  booked_total: number
  is_editable: boolean
}

export interface IProjectsResponse {
  filtered?: IProject[]
  all_count?: number
  active_count?: number
  links?: {
    next: string | null
    previous: string | null
  }
  page_size?: number
  count?: number
  current_page?: number
  total_pages?: number
  from?: number
  to?: number
  results?: IProject[]
}

export interface IProjectsDashboardStats {
  active_count: number
  new_active_count: number
  deleted_count: number
  inactive_count: number
}

export interface ITimeTrackingProjectsResponse {
  filtered: ITimeTrackingProject[]
  all_count: number
}

export interface CountedProjects {
  all_count: number
}

export interface ICustomer {
  id: string | number
  name: string
  address: string
  city: string
  phone_number: string
  email: string
}

export interface IProjectFinancialCalculation {
  total_budget?: number
  accounting_expenditures: number
  accounting_forecast: number
  activities_expenditures: number
}

export interface IProjectPhase {
  id: string | number
  name: string
  active: boolean
  start_date: string | number | Date
  end_date: string | number | Date
  finished: boolean
  color: string
}

export interface IProjectLabel {
  id: string | number
  name: string
  color: string
}

export interface IProjectSorting {
  name: 'name' | 'budget' | ''
  direction: 'asc' | 'desc' | ''
}

export type ViewType = 'list' | 'timeline'

export type InsightsViewType =
  | ''
  | 'departmentCost'
  | 'marginTrendChart'
  | 'utilizationCurve'
  | 'budgetPhasing'
  | 'costPerLabel'
  | 'progressChart'
  | 'costForecast'
  | 'invoiceStatus'

export interface IProjectFilters {
  search?: string
  active?: boolean
  order?: string
  ordering?: string
  includes_deleted?: string
  page?: number
  page_size?: number
  no_pagination?: string
}

export interface IPhasesBulk {
  to_create: IProjectPhase[]
  to_update: IProjectPhase[]
  to_delete: number[]
}

export interface ILabelsBulk {
  to_create: IProjectLabel[]
  to_update: IProjectLabel[]
  to_delete: number[]
}

export interface ILabelMergeTasksBulk {
  label_id: number
  to_add: number[]
  to_remove: number[]
}

export interface IDirectCostsBulk {
  to_create: IDirectCost[]
  to_update: IDirectCost[]
  to_delete: IDirectCost[]
}

export interface IOfferDirectCostsBulk {
  to_create: IOfferDirectCost[]
  to_update: IOfferDirectCost[]
  to_delete: IOfferDirectCost[]
  offer?: IProjectOffer
}

export interface IPassTroughCostsBulk {
  to_create: IPassThroughCost[]
  to_update: IPassThroughCost[]
  to_delete: IPassThroughCost[]
  offer?: IProjectOffer
}

export interface IOfferPassThroughCostsBulk {
  to_create: IOfferPassThroughCost[]
  to_update: IOfferPassThroughCost[]
  to_delete: IOfferPassThroughCost[]
  offer?: IProjectOffer
}

export interface ITimeTrackingPermission {
  project_id: string | number
  task_permission: string
  all_task_access: string
  department_access: boolean
  open_phase_access: boolean
}

export interface IProjectCostCategory {
  id: string | number
  project_billing_rate_id: string | number
  name: string
}

export interface IProjectCostSettings {
  to_create: IProjectCostCategory[]
  to_update: IProjectCostCategory[]
  to_delete: number[]
  billing_rate_type: string
  billing_rate_id: string | number
  individual_employees: IEmployeeIndividualRate[]
}

export interface IProjectDepartmentsChart {
  categories: string[][] | string[]
  series: IProjectDepartmentsSeriesData[]
  maximum_budget: number
}

export interface IProjectDepartmentsSeriesData {
  name: string
  data: number[]
}

export interface ICostsDepartmentChartData {
  name: string
  data: number[]
  type?: 'line' | 'column'
}

export interface IProjectDepartmentsChartData {
  data: ICostsDepartmentChartData[]
  categories: string[]
}

export interface ITrackedHoursByDep {
  id: number | string
  name: string
  tracked_hours: number
  total_hours: number
}

export interface ITrackedHoursByDepSeriesData {
  name: string
  data: number[]
}

export interface IProjectCurrentTasks {
  activities: IProjectActivity[]
  employees: IEmployee[]
}

export interface IProjectCurrentDirectCosts {
  direct_costs: IDirectCost[]
  employees: IEmployee[]
}

export interface IDepHours {
  month: number
  year: number
  hours: string | number
}

interface IDepartmentHourUtil {
  name: string
  hours_utilization: IDepHours[]
}

export interface IInsightsDepartmentHourUtilization {
  data: IDepartmentHourUtil[]
  years: number[]
  type: string
}

export interface IDepartmentTrendHours {
  name: string
  data: number[]
}

export interface IDepartmentTrend {
  categories: string[]
  trend_hours: IDepartmentTrendHours[]
}

export interface IProjectBudgetChanges {
  id: string | number
  prefix: 'increase' | 'decrease' | ''
  amount: number | string
  description: string
  date: string | Date | null
}

export interface IProjectBudgetChangesBulk {
  to_create: IProjectBudgetChanges[]
  to_delete: number[]
}

export interface IBudgetCostTab {
  name: string
  key: string
  id: number
}

export interface IProjectTypeFilter {
  key: string
  name: string
  minWidth: string
  count?: number
}

export interface ICostBySupplier {
  supplier: string
  total: number
  position?: string
}

export interface IProjectRecruitmentStatusChanges {
  id: string | number
  patients: number | string
  sites: number | string
  date: string | Date | null
}

export interface IProjectRecruitmentStatusChangesBulk {
  to_create: IProjectRecruitmentStatusChanges[]
  to_delete: number[]
}

export interface ICurrentProjectListEntry {
  project_name: string
  tasks: number
  hours: number
}

export interface IProjectsHealthWidget {
  on_track: number
  at_risk: number
  off_track: number
}

export interface IProjectHealthChartData {
  series: number[]
  labels: string[]
  colors: string[]
}
